import React from 'react';
import { useServiceWorker } from '../../utils/serviceWorker';
import clsx from 'clsx';

interface ServiceWorkerStatusProps {
  className?: string;
}

export const ServiceWorkerStatus: React.FC<ServiceWorkerStatusProps> = ({
  className,
}) => {
  const { isOffline, needRefresh, offlineReady, updateServiceWorker } = useServiceWorker();

  // Don't show anything if everything is normal
  if (!isOffline && !needRefresh && !offlineReady) {
    return null;
  }

  return (
    <div className={clsx('fixed bottom-4 right-4 z-50', className)}>
      {/* Offline indicator */}
      {isOffline && (
        <div className="mb-2 bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded-lg shadow-lg">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
            <span className="font-medium">You're offline</span>
          </div>
          <p className="text-sm mt-1">
            Some features may be limited. You can still view cached data.
          </p>
        </div>
      )}

      {/* App ready for offline use */}
      {offlineReady && !isOffline && (
        <div className="mb-2 bg-green-100 border border-green-400 text-green-800 px-4 py-3 rounded-lg shadow-lg">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="font-medium">App ready for offline use</span>
          </div>
        </div>
      )}

      {/* Update available */}
      {needRefresh && (
        <div className="bg-blue-100 border border-blue-400 text-blue-800 px-4 py-3 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                />
              </svg>
              <div>
                <span className="font-medium">Update available</span>
                <p className="text-sm">A new version of the app is ready.</p>
              </div>
            </div>
            <button
              onClick={updateServiceWorker}
              className="ml-4 bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Update
            </button>
          </div>
        </div>
      )}
    </div>
  );
};